import { Card, Divider, Flex, Modal, Text, Button, notification, Title } from "components/common"
import { TitleDescription, IconText } from "components/custom"
import { t } from "i18next"
import { forwardRef, useImperativeHandle, useState } from "react"
import { localisedValue } from "utils"
import { instructions, modalTitle } from "../utilis"
import styles from "./psidSuccessModal.module.less"
import theme from "utils/themeVars"
import StructuredList from "components/common/structuredList/structuredList"

function buildWhatsAppShareText(psidDisplay) {
  return t("Your PSID is {{psid}}. Use it in your bank app under Bill Payments / KuickPay to complete payment.", {
    psid: psidDisplay,
  })
}
const successIconProps = {
  hasBackground: true,
  color: "#00a63f",
  size: 20,
  iconContainerStyle: {
    borderRadius: "50%",
    minWidth: 36,
    minHeight: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#00a63f",
    "--icon-bg-color": "#00a63f",
    "--icon-color": "#fff",
  },
}
const PsidSuccessModal = forwardRef(function PsidSuccessModal({ lead, client, isFiler }, ref) {
  const [open, setOpen] = useState(false)
  const [psidData, setPsidData] = useState(null)

  useImperativeHandle(
    ref,
    () => ({
      setVisible(visible, data) {
        if (visible && data != null) {
          setPsidData(data)
        }
        if (!visible) {
          setPsidData(null)
        }
        setOpen(!!visible)
      },
    }),
    []
  )

  const onClose = () => {
    setOpen(false)
    setPsidData(null)
  }

  const clientName = localisedValue({ object: lead?.Client || client, key: "name" }) || lead?.name || ""
  const display = psidData?.psid
  const mins = psidData?.expiresInMinutes ?? 59

  const onCopy = async () => {
    if (!psidData) return
    const raw = psidData.psid || String(display).replace(/\s/g, "")
    try {
      await navigator.clipboard.writeText(raw)
      notification.success({ message: t("PSID copied") })
    } catch {
      notification.error({ message: t("Could not copy") })
    }
  }

  const onShareWhatsApp = () => {
    if (!display) return
    const text = buildWhatsAppShareText(display)
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer")
  }

  return (
    <Modal
      open={open && !!psidData}
      onCancel={onClose}
      okText={t("Done")}
      onOk={onClose}
      width={820}
      title={modalTitle(lead, client, isFiler)}
      destroyOnClose
      getContainer={false}
      maskClosable={false}
    >
      {psidData && (
        <>
          <Card className={styles.successCard} bordered={false} Shadow={false}>
            <Flex vertical gap={16} style={{ width: "100%" }}>
              <Flex justify="space-between" align="flex-start" wrap="wrap" gap={12} style={{ width: "100%" }}>
                <TitleDescription
                  icon="HiCheck"
                  title={t("PSID Generated")}
                  titleClassName="bold"
                  description={t("Share the code below with {{name}} to complete payment.", {
                    name: clientName || t("the client"),
                  })}
                  color="#595959"
                  iconProps={successIconProps}
                />
                <IconText
                  icon="AiFillClockCircle"
                  iconProps={{ size: 18, color: "#d97706" }}
                  title={t("Expires in {{mins}} mins", { mins })}
                  titleProps={{ className: styles.expiryColor, style: { fontWeight: 500, fontSize: 13 } }}
                  gap={6}
                />
              </Flex>
              <Divider style={{ marginTop: 12, marginBottom: 12, borderColor: theme["gray400"] }} />
              <Flex justify="space-between" align="center" wrap="wrap" className={styles.psidRow}>
                <Text className={`bold ${styles.psidCode}`}>{display}</Text>
                <Flex gap={"16px"} align="center" className={styles.psidActions}>
                  <Button type="link" icon="RiFileCopyLine" onClick={onCopy} className={styles.copyLink}>
                    {t("Copy")}
                  </Button>
                  <Button type="link" icon="RiWhatsappFill" onClick={onShareWhatsApp} className={styles.shareLink}>
                    {t("Share")}
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          </Card>

          <Card className={styles.instructionsCard} bordered={false} Shadow={false}>
            <Title level={5} className={styles.instructionsTitle}>
              {t("How to pay using PSID")}
            </Title>
            <Divider className={styles.instructionsDivider} />
            <StructuredList
              type="ordered"
              items={instructions()}
              listTagProps={{ className: styles.instructionsList }}
            />
          </Card>
        </>
      )}
    </Modal>
  )
})

PsidSuccessModal.displayName = "PsidSuccessModal"

export default PsidSuccessModal
